import React, { useState } from 'react';
import { Upload, FileText, CreditCard, CheckCircle, Search, Calendar, Video, Shield, Clock, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';

import { Footer } from '../components/Footer';
import { CompactHeader } from '../components/CompactHeader';

export default function EFilingPage() {
  const [selectedService, setSelectedService] = useState('file-case');
  const [caseNumber, setCaseNumber] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const eFilingServices = [
    {
      icon: FileText,
      title: 'File a New Case',
      description: 'Submit new legal proceedings electronically',
      features: ['Online Form Submission', 'Document Upload', 'Fee Payment', 'Case Number Assignment'],
      timeframe: '24-48 hours',
      status: 'Available'
    },
    {
      icon: Search,
      title: 'Case Status Tracking',
      description: 'Track the progress of your filed cases',
      features: ['Real-time Updates', 'Hearing Dates', 'Court Orders', 'Document Access'],
      timeframe: 'Instant',
      status: 'Available'
    },
    {
      icon: Upload,
      title: 'Document Upload',
      description: 'Submit additional documents to existing cases',
      features: ['Secure Upload', 'Multiple Formats', 'Version Control', 'Confirmation Receipt'],
      timeframe: '1-2 hours',
      status: 'Available'
    },
    {
      icon: CreditCard,
      title: 'Payment Portal',
      description: 'Pay court fees and fines online',
      features: ['Multiple Payment Methods', 'Instant Receipts', 'Payment History', 'Refund Processing'],
      timeframe: 'Instant',
      status: 'Available'
    },
    {
      icon: Video,
      title: 'Virtual Hearings',
      description: 'Attend court sessions remotely',
      features: ['HD Video Conferencing', 'Document Sharing', 'Recording Access', 'Technical Support'],
      timeframe: 'Scheduled',
      status: 'Available'
    },
    {
      icon: CheckCircle,
      title: 'E-Signatures',
      description: 'Digitally sign legal documents',
      features: ['Legal Validity', 'Secure Authentication', 'Audit Trail', 'Certificate Generation'],
      timeframe: 'Instant',
      status: 'Coming Soon'
    }
  ];

  const caseTypes = [
    { type: 'Civil Cases', court: 'High Court', fee: '₦25,000', processing: '3-5 days' },
    { type: 'Criminal Cases', court: 'High Court', fee: '₦15,000', processing: '2-3 days' },
    { type: 'Commercial Disputes', court: 'Commercial Court', fee: '₦35,000', processing: '2-4 days' },
    { type: 'Family Matters', court: 'Family Court', fee: '₦20,000', processing: '3-5 days' },
    { type: 'Magistrate Cases', court: 'Magistrate Court', fee: '₦10,000', processing: '1-2 days' },
    { type: 'Small Claims', court: 'Small Claims Court', fee: '₦5,000', processing: '1-2 days' }
  ];

  const recentCases = [
    { id: 'HC/2024/001234', type: 'Civil', status: 'Under Review', lastUpdate: '2024-01-15', nextHearing: '2024-02-10' },
    { id: 'MC/2024/005678', type: 'Criminal', status: 'Hearing Scheduled', lastUpdate: '2024-01-12', nextHearing: '2024-01-25' },
    { id: 'CC/2024/001890', type: 'Commercial', status: 'Document Pending', lastUpdate: '2024-01-10', nextHearing: '2024-02-05' },
    { id: 'FC/2024/002345', type: 'Family', status: 'Judgment Delivered', lastUpdate: '2024-01-08', nextHearing: 'Closed' }
  ];

  const simulateUpload = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <CompactHeader  />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              E-Filing Services
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Fast, secure, and convenient electronic filing system for all your legal needs
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/20 text-white border-white/30">
                <Shield className="w-4 h-4 mr-2" />
                Secure & Encrypted
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/20 text-white border-white/30">
                <Clock className="w-4 h-4 mr-2" />
                24/7 Access
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/20 text-white border-white/30">
                <CheckCircle className="w-4 h-4 mr-2" />
                Instant Confirmation
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <FileText className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">File New Case</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Start new proceedings</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <Search className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Track Case</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Check case status</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <Upload className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Upload Document</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Submit additional files</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <CreditCard className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Make Payment</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pay fees online</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <Tabs value={selectedService} onValueChange={setSelectedService}>
            <TabsList className="grid w-full grid-cols-3 mb-12">
              <TabsTrigger value="file-case">File New Case</TabsTrigger>
              <TabsTrigger value="track-case">Track Case</TabsTrigger>
              <TabsTrigger value="services">All Services</TabsTrigger>
            </TabsList>

            {/* File New Case Tab */}
            <TabsContent value="file-case">
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
                    File a New Case
                  </h2>
                  
                  <div className="space-y-6">
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle>Case Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label htmlFor="case-type">Case Type</Label>
                          <select className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800">
                            <option>Select case type...</option>
                            <option>Civil Case</option>
                            <option>Criminal Case</option>
                            <option>Commercial Dispute</option>
                            <option>Family Matter</option>
                            <option>Magistrate Case</option>
                            <option>Small Claims</option>
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="court-division">Court Division</Label>
                          <select className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800">
                            <option>Select court...</option>
                            <option>High Court - Civil Division</option>
                            <option>High Court - Criminal Division</option>
                            <option>Commercial Court</option>
                            <option>Family Court</option>
                            <option>Magistrate Court</option>
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="case-title">Case Title</Label>
                          <Input 
                            id="case-title" 
                            placeholder="Enter case title..."
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="case-summary">Case Summary</Label>
                          <textarea 
                            id="case-summary"
                            rows={4}
                            placeholder="Brief description of the case..."
                            className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle>Document Upload</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Click to upload or drag and drop files
                          </p>
                          <Button variant="outline" onClick={simulateUpload}>
                            Choose Files
                          </Button>
                          {uploadProgress > 0 && (
                            <div className="mt-4">
                              <Progress value={uploadProgress} className="w-full" />
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                {uploadProgress === 100 ? 'Upload complete!' : `Uploading... ${uploadProgress}%`}
                              </p>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Supported formats: PDF, DOC, DOCX (Max 10MB per file)
                        </p>
                      </CardContent>
                    </Card>

                    <Button size="lg" className="w-full">
                      Proceed to Payment
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Filing Information
                  </h3>

                  <div className="space-y-6">
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle>Case Types & Fees</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {caseTypes.map((caseType, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{caseType.type}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{caseType.court}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-blue-600">{caseType.fee}</p>
                                <p className="text-xs text-gray-500">{caseType.processing}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle>Required Documents</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            Statement of Claim/Charge
                          </li>
                          <li className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            Affidavit in Support
                          </li>
                          <li className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            Supporting Documents
                          </li>
                          <li className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            Valid ID of Applicant
                          </li>
                          <li className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            Legal Practitioner's Call Certificate (if represented)
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Track Case Tab */}
            <TabsContent value="track-case">
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
                    Track Your Case
                  </h2>
                  
                  <Card className="border-0 shadow-lg mb-8">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="case-number">Case Number</Label>
                          <Input 
                            id="case-number"
                            placeholder="Enter case number (e.g., HC/2024/001234)"
                            value={caseNumber}
                            onChange={(e) => setCaseNumber(e.target.value)}
                          />
                        </div>
                        <Button className="w-full">
                          <Search className="h-4 w-4 mr-2" />
                          Search Case
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Cases */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Recent Cases
                  </h3>
                  <div className="space-y-4">
                    {recentCases.map((case_, index) => (
                      <Card key={index} className="border-0 shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-bold text-gray-900 dark:text-white">{case_.id}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{case_.type} Case</p>
                            </div>
                            <Badge variant={
                              case_.status === 'Under Review' ? 'default' :
                              case_.status === 'Hearing Scheduled' ? 'secondary' :
                              case_.status === 'Document Pending' ? 'destructive' :
                              'secondary'
                            }>
                              {case_.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Last Update:</span>
                              <p className="font-medium text-gray-900 dark:text-white">{case_.lastUpdate}</p>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Next Hearing:</span>
                              <p className="font-medium text-gray-900 dark:text-white">{case_.nextHearing}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Case Status Guide
                  </h3>

                  <div className="space-y-4">
                    <Card className="border-l-4 border-l-blue-600">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Filed</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Your case has been successfully submitted and assigned a case number.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-yellow-500">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Under Review</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          The court is reviewing your submitted documents and case details.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-600">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Hearing Scheduled</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          A hearing date has been set. Check the details for date and time.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Document Pending</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Additional documents are required to proceed with your case.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-purple-600">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Judgment Delivered</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          The court has delivered its final judgment on the case.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* All Services Tab */}
            <TabsContent value="services">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  E-Filing Services
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  Comprehensive digital solutions for all your legal filing needs
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {eFilingServices.map((service, index) => (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-4">
                        <service.icon className="h-8 w-8 text-blue-600" />
                        <Badge variant={service.status === 'Available' ? 'default' : 'secondary'}>
                          {service.status}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                        {service.description}
                      </p>

                      <div className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-sm mb-4">
                        <span className="text-gray-600 dark:text-gray-400">Processing Time:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{service.timeframe}</span>
                      </div>

                      <Button 
                        className="w-full"
                        disabled={service.status !== 'Available'}
                      >
                        {service.status === 'Available' ? 'Get Started' : 'Coming Soon'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Need Help with E-Filing?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Our support team is here to assist you
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <Download className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">User Guide</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Download our comprehensive e-filing guide
                </p>
                <Button variant="outline" size="sm">Download PDF</Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <Video className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Video Tutorials</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Watch step-by-step filing tutorials
                </p>
                <Button variant="outline" size="sm">Watch Videos</Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Technical Support</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Get help with technical issues
                </p>
                <Button variant="outline" size="sm">Contact Support</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
       <Footer />
    </div>
  );
}